import { useSearchParams } from 'react-router';
import type { SetURLSearchParams } from 'react-router';
import { useHydrated } from './use-hydrated';

/** The query-string state EVERY prerendered page was rendered with: none. */
const NO_PARAMS = new URLSearchParams();

/**
 * Did this document arrive as prerendered HTML?
 *
 * `window.__RQ_STATE__` is the serialized query cache entry-server emits right
 * after `<div id="root">` on every prerendered page (see entry-server.render) —
 * so its presence is exactly "this DOM came from the prerender and React is
 * hydrating it". The `@spa` fallback (`dist/app-shell.html`) deliberately ships
 * an EMPTY `#root` and no state script, so an un-prerendered route reads `false`
 * here and is left alone: it mounts with `createRoot`, has no server HTML to
 * agree with, and must read the real URL on its first render or it would fetch
 * the default list and then the real one — two round trips instead of one.
 *
 * Read at render time (not captured at module load) so a test can set it up the
 * same way entry-client does.
 */
function hydratingPrerenderedShell(): boolean {
  return (
    typeof window !== 'undefined' &&
    (window as unknown as { __RQ_STATE__?: unknown }).__RQ_STATE__ != null
  );
}

/**
 * `useSearchParams`, but yielding the PRERENDER's query-string state (i.e. none)
 * for the single render React hydrates with, then the real URL from the next
 * render on.
 *
 * Why this exists. The prerender renders bare paths only — `/`, `/ifi`,
 * `/results`, `/page/2` — never a query string, and nginx serves that one file
 * for the path whatever the query is (`/catalog?q=…` even 301s to `/?q=…`; see
 * deploy/nginx/uztender.com.conf). So on a hard load of `/?q=насос` the server
 * HTML is the DEFAULT catalog list while the first client render, reading `?q=`
 * straight off the URL, wants the search state: a different count line, a
 * skeleton instead of 20 rows, chips that only exist during a search. React
 * calls that a text mismatch, throws **#418**, discards the whole prerendered
 * subtree and rebuilds it client-side.
 *
 * Measured on this build, 2026-09-26, Chromium at ×4 CPU throttle, `/?q=насос`:
 * body text 15 422 chars at 1.0 s (the prerendered paint) → **648 at 2.0 s** (the
 * discarded DOM) → 25 554 at 3.5 s, with `#root h1` no longer even connected to
 * the document. Plain `/` never lost its nodes. Phone QA saw the same shape on a
 * real Android (796 chars at 3.5 s → 25 673 at 11–14 s).
 *
 * Deferring the URL read by one render makes the hydration render byte-identical
 * to the server's, so the prerendered DOM survives; `useHydrated` then flips and
 * the search fires on the very next render — one request, the search's own, with
 * no extra round trip (the default list is already seeded and pinned by
 * `refetchOnMount: false`).
 *
 * Same idiom, and the same reason, as `useHydrated` itself and as Topbar's
 * `useState('')` + effect sync of its search box — which is why Topbar needs no
 * change.
 *
 * ⚠️ WRITES. The setter is react-router's own and always acts on the real URL,
 * but a value built from the params this hook returns (`new
 * URLSearchParams(params)`) must only be written from an EVENT HANDLER — by then
 * hydration is long done and `params` is the real thing. Writing from a mount
 * effect would persist the prerender's empty state and drop the visitor's
 * filters.
 */
export function useShellSearchParams(): [URLSearchParams, SetURLSearchParams] {
  const [params, setParams] = useSearchParams();
  const hydrated = useHydrated();
  const shell = !hydrated && hydratingPrerenderedShell() ? NO_PARAMS : params;
  return [shell, setParams];
}
